import { c as _c } from "react/compiler-runtime";
import { DndContext, DragOverlay } from '@dnd-kit/core';
import { AnimatePresence, motion } from 'framer-motion';
import { match } from 'ts-pattern';
import { startTransition, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef } from 'react';
import { getSafely } from 'shared/web';
import { useHttpCalls } from './api/useHttpCalls';
import { BulkActionsBar } from './components/BulkActionsBar/BulkActionsBar';
import { DefaultEmptyStateRenderer } from './components/DefaultEmptyStateRenderer/DefaultEmptyStateRenderer';
import { DragOverlayColumn } from './components/DragOverlayColumn/DragOverlayColumn';
import { ExternalFiltersPortal } from './components/ExternalFiltersPortal/ExternalFiltersPortal';
import { GridBody } from './components/GridBody/GridBody';
import { GridHeader } from './components/GridHeader/GridHeader';
import { HeaderRow } from './components/HeaderRow/HeaderRow';
import { GridPagination } from './components/Pagination/Pagination';
import { Sidebar } from './components/Sidebar/Sidebar';
import { DEFAULT_COL_WIDTH } from './constants/defaultColWidth';
import { SELECTION_COLUMN_WIDTH } from './constants/selectionColumn';
import { ColumnMenuContext } from './context/ColumnMenuContext';
import { useDataGridContext } from './context/useDataGridContext';
import { useBulkActionExecution } from './hooks/useBulkActionExecution';
import { useBulkActions } from './hooks/useBulkActions';
import { useColumnAutosize } from './hooks/useColumnAutosize';
import { useColumnDrag } from './hooks/useColumnDrag';
import { useColumnResize } from './hooks/useColumnResize';
import { useGridActionColumn } from './hooks/useGridActionColumn';
import { useGridDataFetching } from './hooks/useGridDataFetching';
import { useGridDragAndDrop } from './hooks/useGridDragAndDrop';
import { useGridFilterSync } from './hooks/useGridFilterSync';
import { useGridSelection } from './hooks/useGridSelection';
import { useHandleLink } from './hooks/useHandleLink';
import { useIsMobileView } from './hooks/useIsMobileView';
import { usePageUrlSync } from './hooks/usePageUrlSync';
import { usePresetSync } from './hooks/usePresetSync';
import { useRowActionClick } from './hooks/useRowActionClick';
import { useServerData } from './hooks/useServerData';
import { selectActiveDragId, selectActivePreset, selectColumnGroupsVisible, selectDataQuery, selectIsBulkActionLoading, selectQueryId } from './store/createEagGridStore';
import { useColumnMovingAllowed, useActionColumnEnabled, usePaginationEnabled, useColumnMenuEnabled, useColumnPinningAllowed, useColumnAutoSizingAllowed } from './store/selectors';
import { useActionsDefinition } from './store/selectors/useActionsDefinition';
import { useActivePanel } from './store/selectors/useActivePanel';
import { selectRowSelectMode } from './store/selectors/useBehavior';
import { useColumnStates } from './store/selectors/useColumnStates';
import { useFilterValues } from './store/selectors/useFilterValues';
import { useGridIsLoading } from './store/selectors/useGridIsLoading';
import { useGridPage } from './store/selectors/useGridPage';
import { useGridPreviousRows } from './store/selectors/useGridPreviousRows';
import { useGridRows } from './store/selectors/useGridRows';
import { useGridSettingsSafe } from './store/selectors/useGridSettingsSafe';
import { useGridTotalCount } from './store/selectors/useGridTotalCount';
import { useIsColumnsReady } from './store/selectors/useIsColumnsReady';
import { useIsFilterActive } from './store/selectors/useIsFilterActive';
import { useOrderedVisibleColumns } from './store/selectors/useOrderedVisibleColumns';
import { usePinnedLeftColumns } from './store/selectors/usePinnedLeftColumns';
import { usePinnedRightColumns } from './store/selectors/usePinnedRightColumns';
import { useSelectedRowIds } from './store/selectors/useSelectedRowIds';
import { useVisibleColumns } from './store/selectors/useVisibleColumns';
import { useEagGridSelector } from './store/useEagGridSelector';
import { useEagGridStore } from './store/useEagGridStore';
import type { EagColumn } from './types/EagColumn';
import type { EagColumnState } from './types/EagColumnState';
import type { EagGridProps } from './types/EagGridProps';
import type { EagGridTableInnerProps } from './types/EagGridTableInnerProps';
import type { EagGridTableProps } from './types/EagGridTableProps';
import type { RowData } from './types/RowData';
import { ToolpanelType } from './types/ToolpanelType';
import { columnToInitialState } from './utils/columnToInitialState';
import { computeColIndexMap } from './utils/computeColIndexMap';
import { computeSortModelKey } from './utils/computeSortModelKey';
import { flattenColumnConfig } from './utils/flattenColumnConfig';
import { getColumnData } from './utils/getColumnData';
import { isFilterValueActive } from './utils/isFilterValueActive';
import { LineHeightToPx } from './utils/lineHeightToValue';
import { setSourceSearchParamsByGridCode } from './utils/url/setSourceSearchParamsByGridCode';
const BULK_BAR_ANIMATION = {
  initial: {
    height: 0,
    opacity: 0,
    overflow: 'hidden'
  },
  animate: {
    height: 'auto',
    opacity: 1,
    overflow: 'hidden'
  },
  exit: {
    height: 0,
    opacity: 0,
    overflow: 'hidden'
  },
  transition: {
    duration: 0.2,
    ease: 'easeInOut'
  }
};

/**
 * Core grid component that owns the table layout, header, sidebar, and data
 * fetching lifecycle for a single EagGrid instance.
 *
 * ## State management — why no prop drilling
 *
 * EagGridTable deliberately does **not** accept or forward low-level state slices
 * (loading, rows, column widths, drag state, etc.) as props.  Instead it reads
 * and mutates state through `useEagGridStore()` / `useEagGridSelector()`, which
 * reach the store provided by the parent `EagGridStoreProvider`.
 *
 * This keeps the component surface small and prevents prop explosion as new UI
 * features are added.  Specifically for the filter system redesign:
 *
 * - `filterValues` flows from the store into `handleFilterChange`
 *   and `handleResetFilters`, then gets passed to the sidebar as callbacks —
 *   no intermediate component re-render required.
 * - `activePanel` lives in the store; GridHeader toggles it, Sidebar
 *   reads it, and the new FilterPanel close button dispatches `setActivePanel(null)`
 *   — all without EagGridTable acting as a message bus.
 * - Drag state (`activeDragId`, `overColumnId`, `dropSide`) is written here and
 *   read directly by `HeaderCell` / `GridCell` 4 levels down — zero
 *   intermediary props.
 *
 * ## Column config stability
 *
 * `columnConfig` is treated as stable after mount.  Dynamic column changes require
 * remounting the component (pass a new `key` prop to the parent `<EagGrid>`).
 * This is enforced by the empty-dep `useLayoutEffect` that initialises column states.
 *
 * ## Filter panel integration
 *
 * `handleFilterChange` and `handleResetFilters` write directly to the store via
 * `store.setFilterValues`. The sidebar `FilterPanel` receives these callbacks
 * without any context subscription re-renders.
 */
function EagGridTableInner(props) {
  const $ = _c(231);
  const autoHeight = props.autoHeight;
  const gridCode = props.gridCode;
  const columnConfig = props.columnConfig;
  const definition = props.definition;
  let t0;
  if ($[0] !== props.emptyState) {
    t0 = <DefaultEmptyStateRenderer emptyStatus={props.emptyState} />;
    $[0] = props.emptyState;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const emptyStateNode = t0;
  const actionsDefinition = useActionsDefinition();
  const onRowSelectionChange = props.onRowSelectionChange;
  const testId = props["data-testid"];
  const queryModifier = props.queryModifier;
  const predefinedColumns = props.predefinedColumns;
  const actionCallback = props.actionCallback;
  const gridContext = useDataGridContext();
  const store = useEagGridStore();
  const filterValues = useFilterValues();
  const isFilterActive = useIsFilterActive();
  const columnMovingAllowed = useColumnMovingAllowed();
  const actionColumnEnabled = useActionColumnEnabled();
  const paginationEnabled = usePaginationEnabled();
  const columnMenuEnabled = useColumnMenuEnabled();
  const columnPinningAllowed = useColumnPinningAllowed();
  const columnAutoSizingAllowed = useColumnAutoSizingAllowed();
  let t1;
  let t2;
  if ($[2] !== columnConfig || $[3] !== store) {
    t1 = () => {
      const flatCols = flattenColumnConfig(columnConfig);
      store.setColumnStates(flatCols.map(columnToInitialState));
    };
    t2 = [columnConfig, store];
    $[2] = columnConfig;
    $[3] = store;
    $[4] = t1;
    $[5] = t2;
  } else {
    t1 = $[4];
    t2 = $[5];
  }
  useLayoutEffect(t1, t2);
  const dataQuery = useEagGridSelector(selectDataQuery);
  const selectedRowIds = useSelectedRowIds();
  const rowSelectMode = useEagGridSelector(selectRowSelectMode);
  const hasCheckboxColumn = rowSelectMode !== "NONE";
  const page = useGridPage();
  const columnStates = useColumnStates();
  const visibleColumnStates = useVisibleColumns();
  const orderedVisibleColumnStates = useOrderedVisibleColumns();
  const pinnedLeftColumns = usePinnedLeftColumns();
  const pinnedRightColumns = usePinnedRightColumns();
  const isColumnsReady = useIsColumnsReady();
  let t3;
  if ($[6] !== orderedVisibleColumnStates) {
    t3 = orderedVisibleColumnStates.filter(_temp);
    $[6] = orderedVisibleColumnStates;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const nonRightPinnedColumnStates = t3;
  const rightPinnedColumnStates = orderedVisibleColumnStates.filter(_temp2);
  let t4;
  if ($[8] !== hasCheckboxColumn || $[9] !== nonRightPinnedColumnStates.length || $[10] !== orderedVisibleColumnStates) {
    t4 = computeColIndexMap(orderedVisibleColumnStates, nonRightPinnedColumnStates.length, hasCheckboxColumn);
    $[8] = hasCheckboxColumn;
    $[9] = nonRightPinnedColumnStates.length;
    $[10] = orderedVisibleColumnStates;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const colIndexMap = t4;
  let t5;
  if ($[12] !== columnConfig) {
    t5 = flattenColumnConfig(columnConfig);
    $[12] = columnConfig;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const flatColumns = t5;
  let t6;
  if ($[14] !== columnStates) {
    t6 = new Map(columnStates.map(_temp3));
    $[14] = columnStates;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const columnWidthMap = t6;
  let t7;
  if ($[16] !== columnWidthMap) {
    t7 = key => columnWidthMap.get(key) ?? DEFAULT_COL_WIDTH;
    $[16] = columnWidthMap;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  const getWidth = t7;
  const colgroupRef = useRef(null);
  const tableRef = useRef(null);
  const resizeManager = useColumnResize(colgroupRef, getWidth);
  const dragManager = useColumnDrag(visibleColumnStates, store.reorderColumnsByKey);
  const handleLink = useHandleLink();
  let t8;
  if ($[18] !== gridCode) {
    t8 = {
      gridCode
    };
    $[18] = gridCode;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  const http = useHttpCalls(t8);
  let t9;
  if ($[20] !== dragManager || $[21] !== store || $[22] !== visibleColumnStates) {
    t9 = {
      visibleColumnStates,
      store,
      dragManager
    };
    $[20] = dragManager;
    $[21] = store;
    $[22] = visibleColumnStates;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  const dnd = useGridDragAndDrop(t9);
  let offsets;
  if ($[24] !== hasCheckboxColumn || $[25] !== pinnedLeftColumns) {
    offsets = new Map();
    let accum = hasCheckboxColumn ? SELECTION_COLUMN_WIDTH : 0;
    for (const cs_2 of pinnedLeftColumns) {
      offsets.set(cs_2.key, accum);
      accum = accum + (cs_2.width ?? DEFAULT_COL_WIDTH);
    }
    $[24] = hasCheckboxColumn;
    $[25] = pinnedLeftColumns;
    $[26] = offsets;
  } else {
    offsets = $[26];
  }
  const pinnedLeftOffsets = offsets;
  let t10;
  if ($[27] !== visibleColumnStates) {
    t10 = visibleColumnStates.filter(_temp4).map(_temp5);
    $[27] = visibleColumnStates;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  const sortModel = t10;
  let t11;
  if ($[29] !== sortModel) {
    t11 = computeSortModelKey(sortModel);
    $[29] = sortModel;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  const sortModelKey = t11;
  const gridSettings = useGridSettingsSafe();
  const itemsPerPage = gridSettings.itemsPerPage;
  const offset = page * itemsPerPage;
  const isMobile = useIsMobileView();
  let t12;
  if ($[31] !== gridCode || $[32] !== predefinedColumns || $[33] !== queryModifier) {
    t12 = {
      gridCode,
      queryModifier,
      predefinedColumns
    };
    $[31] = gridCode;
    $[32] = predefinedColumns;
    $[33] = queryModifier;
    $[34] = t12;
  } else {
    t12 = $[34];
  }
  const serverData = useServerData(t12);
  const rows = useGridRows();
  const previousRows = useGridPreviousRows();
  const isLoading = useGridIsLoading();
  const isEmptyState = rows.length === 0 && !isLoading && isColumnsReady;
  const totalCount = useGridTotalCount();
  const queryId = useEagGridSelector(selectQueryId);
  let t13;
  if ($[35] !== actionColumnEnabled || $[36] !== actionsDefinition || $[37] !== gridSettings || $[38] !== isMobile || $[39] !== previousRows || $[40] !== rows) {
    t13 = {
      rows,
      previousRows,
      actionsDefinition,
      gridSettings,
      actionColumnEnabled,
      isMobile
    };
    $[35] = actionColumnEnabled;
    $[36] = actionsDefinition;
    $[37] = gridSettings;
    $[38] = isMobile;
    $[39] = previousRows;
    $[40] = rows;
    $[41] = t13;
  } else {
    t13 = $[41];
  }
  const {
    actionColumnWidth
  } = useGridActionColumn(t13);
  let offsets_0;
  if ($[42] !== actionColumnWidth || $[43] !== pinnedRightColumns) {
    offsets_0 = new Map();
    let accum_0 = actionColumnWidth;
    for (const cs_5 of [...pinnedRightColumns].reverse()) {
      offsets_0.set(cs_5.key, accum_0);
      accum_0 = accum_0 + (cs_5.width ?? DEFAULT_COL_WIDTH);
    }
    $[42] = actionColumnWidth;
    $[43] = pinnedRightColumns;
    $[44] = offsets_0;
  } else {
    offsets_0 = $[44];
  }
  const pinnedRightOffsets = offsets_0;
  let t14;
  if ($[45] !== filterValues || $[46] !== gridCode || $[47] !== gridSettings || $[48] !== offset || $[49] !== props.refreshRef || $[50] !== queryId || $[51] !== serverData || $[52] !== sortModelKey || $[53] !== store) {
    t14 = {
      gridCode,
      store,
      serverData,
      offset,
      filterValues,
      sortModelKey,
      gridSettings,
      queryId,
      refreshRef: props.refreshRef
    };
    $[45] = filterValues;
    $[46] = gridCode;
    $[47] = gridSettings;
    $[48] = offset;
    $[49] = props.refreshRef;
    $[50] = queryId;
    $[51] = serverData;
    $[52] = sortModelKey;
    $[53] = store;
    $[54] = t14;
  } else {
    t14 = $[54];
  }
  const {
    refreshData,
    serverDataRef,
    offsetRef,
    queryIdRef
  } = useGridDataFetching(t14);
  const activeDragId = useEagGridSelector(selectActiveDragId);
  const columnGroupsVisible = useEagGridSelector(selectColumnGroupsVisible);
  const activePresetFromStore = useEagGridSelector(selectActivePreset);
  const activePanel = useActivePanel();
  let t15;
  if ($[55] !== store) {
    t15 = panel => {
      const current = store.getState().activePanel;
      store.setActivePanel(current === panel ? null : panel);
    };
    $[55] = store;
    $[56] = t15;
  } else {
    t15 = $[56];
  }
  const handleTogglePanel = t15;
  let t16;
  if ($[57] !== store) {
    t16 = () => {
      store.setActivePanel(null);
    };
    $[57] = store;
    $[58] = t16;
  } else {
    t16 = $[58];
  }
  const handleClosePanel = t16;
  let t17;
  if ($[59] !== store) {
    t17 = () => {
      if (store.getState().activePanel != null) {
        store.setActivePanel(null);
      }
    };
    $[59] = store;
    $[60] = t17;
  } else {
    t17 = $[60];
  }
  const handleTableAreaClick = t17;
  const filterValuesRef = useRef(filterValues);
  let t18;
  if ($[61] !== store) {
    t18 = (key_0, value) => {
      store.deselectAll();
      const next = {
        ...filterValuesRef.current
      };
      if (isFilterValueActive(value)) {
        next[key_0] = value as Record<string, unknown>;
      } else {
        delete next[key_0];
      }
      store.setFilterValues(next);
    };
    $[61] = store;
    $[62] = t18;
  } else {
    t18 = $[62];
  }
  const handleFilterChange = t18;
  let t19;
  if ($[63] !== store) {
    t19 = () => {
      store.deselectAll();
      store.resetFilterValues();
    };
    $[63] = store;
    $[64] = t19;
  } else {
    t19 = $[64];
  }
  const handleResetFilters = t19;
  let t20;
  if ($[65] !== flatColumns) {
    t20 = new Map(flatColumns.map(_temp6));
    $[65] = flatColumns;
    $[66] = t20;
  } else {
    t20 = $[66];
  }
  const flatColumnMap = t20;
  const {
    autosizeColumn: handleAutosizeColumn,
    autosizeAllColumns: handleAutosizeAllColumns
  } = useColumnAutosize(tableRef, flatColumnMap, columnMenuEnabled);
  let t21;
  if ($[67] !== http || $[68] !== store) {
    t21 = () => {
      store.setIsLoadingWithSnapshot();
      http.deleteVirtualPreset().then(freshPreset => {
        if (freshPreset) {
          store.applyPresetColumnsAndSort(freshPreset.columnsSettings);
        } else {
          store.setIsLoading(false);
        }
      });
    };
    $[67] = http;
    $[68] = store;
    $[69] = t21;
  } else {
    t21 = $[69];
  }
  const handleResetColumns = t21;
  let t22;
  if ($[70] !== store) {
    t22 = (key_1, pinned) => {
      store.setColumnPinned(key_1, pinned);
    };
    $[70] = store;
    $[71] = t22;
  } else {
    t22 = $[71];
  }
  const handlePinColumn = t22;
  let t23;
  if ($[72] !== store) {
    t23 = (key_2, sort) => {
      startTransition(() => {
        store.setColumnSort(key_2, sort);
      });
    };
    $[72] = store;
    $[73] = t23;
  } else {
    t23 = $[73];
  }
  const handleSortDirect = t23;
  let t24;
  if ($[74] !== activePresetFromStore || $[75] !== columnStates || $[76] !== filterValues || $[77] !== gridSettings || $[78] !== store.setFilterValues) {
    t24 = {
      columnStates,
      filters: filterValues,
      onFiltersChange: store.setFilterValues,
      activePreset: activePresetFromStore,
      settings: gridSettings
    };
    $[74] = activePresetFromStore;
    $[75] = columnStates;
    $[76] = filterValues;
    $[77] = gridSettings;
    $[78] = store.setFilterValues;
    $[79] = t24;
  } else {
    t24 = $[79];
  }
  usePresetSync(t24);
  let t25;
  if ($[80] !== onRowSelectionChange || $[81] !== rowSelectMode || $[82] !== rows || $[83] !== selectedRowIds || $[84] !== store) {
    t25 = {
      store,
      rowSelectMode,
      selectedRowIds,
      rows,
      onRowSelectionChange
    };
    $[80] = onRowSelectionChange;
    $[81] = rowSelectMode;
    $[82] = rows;
    $[83] = selectedRowIds;
    $[84] = store;
    $[85] = t25;
  } else {
    t25 = $[85];
  }
  const {
    handleToggleSelection,
    handleDeselectAll
  } = useGridSelection(t25);
  let t26;
  if ($[86] !== filterValues || $[87] !== gridContext.onExternalFilterChange) {
    t26 = {
      filterValues,
      onExternalFilterChange: gridContext.onExternalFilterChange
    };
    $[86] = filterValues;
    $[87] = gridContext.onExternalFilterChange;
    $[88] = t26;
  } else {
    t26 = $[88];
  }
  useGridFilterSync(t26);
  let t27;
  if ($[89] !== filterValues) {
    t27 = () => {
      filterValuesRef.current = filterValues;
    };
    $[89] = filterValues;
    $[90] = t27;
  } else {
    t27 = $[90];
  }
  useEffect(t27);
  let t28;
  if ($[91] !== columnStates) {
    t28 = new Map(columnStates.map(_temp7));
    $[91] = columnStates;
    $[92] = t28;
  } else {
    t28 = $[92];
  }
  const columnStateMap = t28;
  let t29;
  if ($[93] !== store) {
    t29 = key_3 => {
      const sortMode = store.getState().definition?.behavior?.columnSortMode;
      if (sortMode === "NONE") {
        return;
      }
      const currentState = store.getState().columnStates.find(cs_7 => cs_7.key === key_3);
      if (!currentState) {
        return;
      }
      const nextSort = match(currentState.sort).with(null, _temp8).with(undefined, _temp9).with("ASC", _temp0).with("DESC", _temp1).exhaustive();
      startTransition(() => {
        store.setColumnSort(key_3, nextSort);
      });
    };
    $[93] = store;
    $[94] = t29;
  } else {
    t29 = $[94];
  }
  const handleSort = t29;
  let t30;
  if ($[95] !== actionCallback || $[96] !== actionsDefinition || $[97] !== gridCode || $[98] !== handleLink || $[99] !== offsetRef || $[100] !== queryIdRef || $[101] !== rowSelectMode || $[102] !== serverDataRef || $[103] !== store) {
    t30 = (row, event) => {
      match(rowSelectMode).with("MULTIPLE", () => store.toggleRowSelection(row.id)).with("SINGLE", () => store.setSingleSelection(row.id)).with("NONE", _temp10).exhaustive();
      const rowClickActionKey = row.actions.rowClick ?? null;
      if (rowClickActionKey && actionCallback && actionsDefinition && getSafely(actionsDefinition, rowClickActionKey)) {
        const result = actionCallback({
          actionKey: rowClickActionKey,
          rowId: row.id,
          getColumnData: getColumnData(row, gridCode),
          rowData: row,
          refreshData: () => serverDataRef.current.fetchPage(offsetRef.current),
          refreshCells: _temp11,
          redrawRows: _temp12,
          deselectAll: () => store.deselectAll(),
          sourceSearchParams: setSourceSearchParamsByGridCode(gridCode, String(row.rowNumber)),
          queryId: queryIdRef.current ?? "",
          actionLock: {
            lock: _temp13,
            unlock: _temp14
          }
        });
        if (typeof result === "string") {
          handleLink(result, event.nativeEvent as MouseEvent);
        }
      }
    };
    $[95] = actionCallback;
    $[96] = actionsDefinition;
    $[97] = gridCode;
    $[98] = handleLink;
    $[99] = offsetRef;
    $[100] = queryIdRef;
    $[101] = rowSelectMode;
    $[102] = serverDataRef;
    $[103] = store;
    $[104] = t30;
  } else {
    t30 = $[104];
  }
  const handleRowClick = t30;
  let t31;
  if ($[105] !== offsetRef || $[106] !== serverDataRef) {
    t31 = () => serverDataRef.current.fetchPage(offsetRef.current);
    $[105] = offsetRef;
    $[106] = serverDataRef;
    $[107] = t31;
  } else {
    t31 = $[107];
  }
  const handleActionClick = useRowActionClick(actionCallback, gridCode, queryIdRef as React.RefObject<string>, t31);
  let t32;
  if ($[108] !== flatColumns || $[109] !== orderedVisibleColumnStates) {
    const map = new Map(flatColumns.map(_temp15));
    t32 = orderedVisibleColumnStates.map(cs_8 => map.get(cs_8.key)).filter(_temp16);
    $[108] = flatColumns;
    $[109] = orderedVisibleColumnStates;
    $[110] = t32;
  } else {
    t32 = $[110];
  }
  const visibleColumns = t32;
  let t33;
  bb0: {
    if (selectedRowIds.size === 0) {
      let t34;
      if ($[111] === Symbol.for("react.memo_cache_sentinel")) {
        t34 = [];
        $[111] = t34;
      } else {
        t34 = $[111];
      }
      t33 = t34;
      break bb0;
    }
    let t34;
    if ($[112] !== rows || $[113] !== selectedRowIds) {
      let t35;
      if ($[115] !== selectedRowIds) {
        t35 = row_0 => selectedRowIds.has(row_0.id);
        $[115] = selectedRowIds;
        $[116] = t35;
      } else {
        t35 = $[116];
      }
      t34 = rows.filter(t35).map(_temp17);
      $[112] = rows;
      $[113] = selectedRowIds;
      $[114] = t34;
    } else {
      t34 = $[114];
    }
    t33 = t34;
  }
  const selectedRowsActions = t33;
  let t34;
  if ($[117] !== actionsDefinition) {
    t34 = actionsDefinition ?? {};
    $[117] = actionsDefinition;
    $[118] = t34;
  } else {
    t34 = $[118];
  }
  const bulkActions = useBulkActions(t34, selectedRowsActions);
  let t35;
  if ($[119] !== store) {
    t35 = p => {
      store.deselectAll();
      store.setPage(p - 1);
    };
    $[119] = store;
    $[120] = t35;
  } else {
    t35 = $[120];
  }
  const handlePageChange = t35;
  let t36;
  if ($[121] !== filterValues) {
    t36 = filterValues ?? {};
    $[121] = filterValues;
    $[122] = t36;
  } else {
    t36 = $[122];
  }
  const t37 = queryId ?? "";
  let t38;
  if ($[123] !== gridCode || $[124] !== page || $[125] !== t36 || $[126] !== t37) {
    t38 = {
      gridCode,
      page,
      filterValues: t36,
      queryId: t37
    };
    $[123] = gridCode;
    $[124] = page;
    $[125] = t36;
    $[126] = t37;
    $[127] = t38;
  } else {
    t38 = $[127];
  }
  usePageUrlSync(t38);
  let t39;
  if ($[128] !== visibleColumns) {
    t39 = new Map(visibleColumns.map(_temp18));
    $[128] = visibleColumns;
    $[129] = t39;
  } else {
    t39 = $[129];
  }
  const visibleColumnMap = t39;
  let t40;
  if ($[130] !== orderedVisibleColumnStates) {
    t40 = new Map(orderedVisibleColumnStates.map(_temp19));
    $[130] = orderedVisibleColumnStates;
    $[131] = t40;
  } else {
    t40 = $[131];
  }
  const visibleColumnStateMap = t40;
  let t41;
  if ($[132] !== activeDragId || $[133] !== visibleColumnMap) {
    t41 = activeDragId ? visibleColumnMap.get(activeDragId) ?? null : null;
    $[132] = activeDragId;
    $[133] = visibleColumnMap;
    $[134] = t41;
  } else {
    t41 = $[134];
  }
  const dragOverlayColumn = t41;
  let t42;
  if ($[135] !== activeDragId || $[136] !== visibleColumnStateMap) {
    t42 = activeDragId ? visibleColumnStateMap.get(activeDragId) ?? null : null;
    $[135] = activeDragId;
    $[136] = visibleColumnStateMap;
    $[137] = t42;
  } else {
    t42 = $[137];
  }
  const dragOverlayColumnState = t42;
  let t43;
  if ($[138] !== actionCallback || $[139] !== gridCode || $[140] !== refreshData || $[141] !== rows || $[142] !== selectedRowIds) {
    t43 = {
      actionCallback,
      rows,
      selectedRowIds,
      gridCode,
      refreshData
    };
    $[138] = actionCallback;
    $[139] = gridCode;
    $[140] = refreshData;
    $[141] = rows;
    $[142] = selectedRowIds;
    $[143] = t43;
  } else {
    t43 = $[143];
  }
  const handleBulkAction = useBulkActionExecution(t43);
  const isBulkActionLoading = useEagGridSelector(selectIsBulkActionLoading);
  const lastPinnedLeftKey = pinnedLeftColumns.length > 0 ? pinnedLeftColumns[pinnedLeftColumns.length - 1]?.key ?? null : null;
  const firstPinnedRightKey = pinnedRightColumns.length > 0 ? pinnedRightColumns[0]?.key ?? null : null;
  const showColumnMenu = columnMenuEnabled;
  const showPinning = columnPinningAllowed;
  const showAutosize = columnAutoSizingAllowed;
  let t44;
  if ($[144] !== filterValues) {
    t44 = filterValues ?? {};
    $[144] = filterValues;
    $[145] = t44;
  } else {
    t44 = $[145];
  }
  let t45;
  if ($[146] !== columnStateMap || $[147] !== handleAutosizeAllColumns || $[148] !== handleAutosizeColumn || $[149] !== handleFilterChange || $[150] !== handlePinColumn || $[151] !== handleResetColumns || $[152] !== handleResetFilters || $[153] !== handleSortDirect || $[154] !== showAutosize || $[155] !== showColumnMenu || $[156] !== showPinning || $[157] !== t44) {
    t45 = {
      showColumnMenu,
      showPinning,
      showAutosize,
      filterState: t44,
      columnStates: columnStateMap,
      onFilterChange: handleFilterChange,
      onPinColumn: handlePinColumn,
      onAutosizeColumn: handleAutosizeColumn,
      onAutosizeAllColumns: handleAutosizeAllColumns,
      onResetColumns: handleResetColumns,
      onResetFilters: handleResetFilters,
      onSort: handleSortDirect
    };
    $[146] = columnStateMap;
    $[147] = handleAutosizeAllColumns;
    $[148] = handleAutosizeColumn;
    $[149] = handleFilterChange;
    $[150] = handlePinColumn;
    $[151] = handleResetColumns;
    $[152] = handleResetFilters;
    $[153] = handleSortDirect;
    $[154] = showAutosize;
    $[155] = showColumnMenu;
    $[156] = showPinning;
    $[157] = t44;
    $[158] = t45;
  } else {
    t45 = $[158];
  }
  const columnMenuContextValue = t45;
  const t46 = autoHeight ? "overflow-x-auto overflow-y-visible w-full min-w-0 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]" : "flex-1 overflow-auto w-full min-w-0 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]";
  const t47 = isColumnsReady ? <table ref={tableRef} className="w-full text-sm table-fixed" style={{
    borderCollapse: "separate",
    borderSpacing: 0
  }} aria-label={gridCode}><colgroup ref={colgroupRef}>{hasCheckboxColumn && <col style={{
        width: SELECTION_COLUMN_WIDTH
      }} />}{nonRightPinnedColumnStates.map(_temp20)}<col />{rightPinnedColumnStates.map(_temp21)}{actionColumnWidth > 0 && <col style={{
        width: actionColumnWidth
      }} />}</colgroup><ColumnMenuContext.Provider value={columnMenuContextValue}><HeaderRow columns={visibleColumns} columnStates={orderedVisibleColumnStates} onSort={handleSort} onResizeStart={resizeManager.onResizeStart} sortableIds={dragManager.sortableIds} pinnedLeftOffsets={pinnedLeftOffsets} pinnedRightOffsets={pinnedRightOffsets} columnConfig={columnConfig} columnGroupsVisible={columnGroupsVisible} lastPinnedLeftKey={lastPinnedLeftKey} firstPinnedRightKey={firstPinnedRightKey} actionColumnWidth={actionColumnWidth > 0 ? actionColumnWidth : undefined} headerHeightPx={LineHeightToPx[gridSettings.headerHeight]} colIndexMap={colIndexMap} rowSelectMode={rowSelectMode} /></ColumnMenuContext.Provider><GridBody columns={visibleColumns} columnStates={orderedVisibleColumnStates} rows={rows} previousRows={previousRows} skeletonRowCount={itemsPerPage} isLoading={isLoading} selectedRowIds={selectedRowIds} onRowClick={handleRowClick} pinnedLeftOffsets={pinnedLeftOffsets} pinnedRightOffsets={pinnedRightOffsets} stripedRows={gridSettings.stripedRows} lastPinnedLeftKey={lastPinnedLeftKey} firstPinnedRightKey={firstPinnedRightKey} onActionClick={actionColumnWidth > 0 ? handleActionClick : undefined} actionColumnWidth={actionColumnWidth > 0 ? actionColumnWidth : undefined} isMobile={isMobile} disableAllActions={store.getState().disableAllActions} rowSelectMode={rowSelectMode} onToggleSelection={handleToggleSelection} /></table> : <table className="w-full text-sm" style={{
    borderCollapse: "separate",
    borderSpacing: 0
  }}><tbody>{Array.from({
        length: itemsPerPage
      }, (_, i) => <tr key={i} style={{
        height: LineHeightToPx[gridSettings.rowHeight]
      }}><td className="p-2"><div className="animate-pulse bg-gray-200 h-4 rounded" /></td></tr>)}</tbody></table>;
  let t48;
  if ($[159] !== t46 || $[160] !== t47) {
    t48 = <div className={t46}>{t47}</div>;
    $[159] = t46;
    $[160] = t47;
    $[161] = t48;
  } else {
    t48 = $[161];
  }
  const tableScrollArea = t48;
  const t49 = autoHeight ? "flex flex-col overflow-hidden min-w-0 relative" : "flex flex-col h-full overflow-hidden min-w-0 relative";
  let t50;
  if ($[162] !== gridCode) {
    t50 = {
      gridCode
    };
    $[162] = gridCode;
    $[163] = t50;
  } else {
    t50 = $[163];
  }
  const t51 = t50 as EagGridProps;
  let t52;
  if ($[164] !== activePanel || $[165] !== columnStates || $[166] !== dataQuery || $[167] !== definition || $[168] !== gridSettings || $[169] !== handleResetFilters || $[170] !== handleTogglePanel || $[171] !== isFilterActive || $[172] !== refreshData || $[173] !== t51 || $[174] !== testId) {
    t52 = <GridHeader definition={definition} dataGridSettings={gridSettings} dataGridProps={t51} columnStates={columnStates} refreshData={refreshData} dataQuery={dataQuery} activePanel={activePanel} onTogglePanel={handleTogglePanel} isFilterActive={isFilterActive} onResetFilters={handleResetFilters} data-testid={testId} />;
    $[164] = activePanel;
    $[165] = columnStates;
    $[166] = dataQuery;
    $[167] = definition;
    $[168] = gridSettings;
    $[169] = handleResetFilters;
    $[170] = handleTogglePanel;
    $[171] = isFilterActive;
    $[172] = refreshData;
    $[173] = t51;
    $[174] = testId;
    $[175] = t52;
  } else {
    t52 = $[175];
  }
  let t53;
  if ($[176] !== bulkActions || $[177] !== handleBulkAction || $[178] !== handleDeselectAll || $[179] !== isBulkActionLoading || $[180] !== selectedRowIds.size || $[181] !== store) {
    t53 = selectedRowIds.size > 0 && <motion.div initial={BULK_BAR_ANIMATION.initial} animate={BULK_BAR_ANIMATION.animate} exit={BULK_BAR_ANIMATION.exit} transition={BULK_BAR_ANIMATION.transition}><BulkActionsBar selectedCount={selectedRowIds.size} bulkActions={bulkActions} onDeselectAll={handleDeselectAll} onBulkAction={handleBulkAction} disableAllActions={store.getState().disableAllActions} isLoading={isBulkActionLoading} /></motion.div>;
    $[176] = bulkActions;
    $[177] = handleBulkAction;
    $[178] = handleDeselectAll;
    $[179] = isBulkActionLoading;
    $[180] = selectedRowIds.size;
    $[181] = store;
    $[182] = t53;
  } else {
    t53 = $[182];
  }
  let t54;
  if ($[183] !== t53) {
    t54 = <AnimatePresence>{t53}</AnimatePresence>;
    $[183] = t53;
    $[184] = t54;
  } else {
    t54 = $[184];
  }
  const t55 = autoHeight ? "flex flex-row overflow-hidden relative min-w-0" : "flex flex-row flex-1 overflow-hidden relative min-w-0";
  const t56 = autoHeight ? "flex flex-col overflow-hidden relative min-w-0" : "flex flex-col flex-1 overflow-hidden relative min-w-0";
  let t57;
  if ($[185] !== columnMovingAllowed || $[186] !== dnd || $[187] !== dragOverlayColumn || $[188] !== dragOverlayColumnState || $[189] !== rows || $[190] !== tableScrollArea) {
    t57 = columnMovingAllowed ? <DndContext sensors={dnd.sensors} onDragStart={dnd.handleDragStart} onDragOver={dnd.handleDragOver} onDragEnd={dnd.handleDragEnd} onDragCancel={dnd.handleDragCancel}>{tableScrollArea}<DragOverlay>{dragOverlayColumn && dragOverlayColumnState ? <DragOverlayColumn column={dragOverlayColumn} columnState={dragOverlayColumnState} rows={rows} /> : null}</DragOverlay></DndContext> : tableScrollArea;
    $[185] = columnMovingAllowed;
    $[186] = dnd;
    $[187] = dragOverlayColumn;
    $[188] = dragOverlayColumnState;
    $[189] = rows;
    $[190] = tableScrollArea;
    $[191] = t57;
  } else {
    t57 = $[191];
  }
  let t58;
  if ($[192] !== emptyStateNode || $[193] !== isEmptyState) {
    t58 = isEmptyState && <div className="absolute inset-0 flex items-center justify-center pointer-events-none"><div className="pointer-events-auto">{emptyStateNode}</div></div>;
    $[192] = emptyStateNode;
    $[193] = isEmptyState;
    $[194] = t58;
  } else {
    t58 = $[194];
  }
  let t59;
  if ($[195] !== handlePageChange || $[196] !== itemsPerPage || $[197] !== page || $[198] !== paginationEnabled || $[199] !== totalCount) {
    t59 = paginationEnabled && totalCount > 0 && <GridPagination currentPage={page + 1} totalPages={Math.ceil(totalCount / itemsPerPage)} totalCount={totalCount} itemsPerPage={itemsPerPage} onPageChange={handlePageChange} />;
    $[195] = handlePageChange;
    $[196] = itemsPerPage;
    $[197] = page;
    $[198] = paginationEnabled;
    $[199] = totalCount;
    $[200] = t59;
  } else {
    t59 = $[200];
  }
  let t60;
  if ($[201] !== handleTableAreaClick || $[202] !== t56 || $[203] !== t57 || $[204] !== t58 || $[205] !== t59) {
    t60 = <div className={t56} onClick={handleTableAreaClick}>{t57}{t58}{t59}</div>;
    $[201] = handleTableAreaClick;
    $[202] = t56;
    $[203] = t57;
    $[204] = t58;
    $[205] = t59;
    $[206] = t60;
  } else {
    t60 = $[206];
  }
  const t61 = filterValues as Record<string, unknown>;
  const t62 = gridContext.externalFilterId ? undefined : gridContext.filterComponent;
  let t63;
  if ($[207] !== activePanel || $[208] !== columnConfig || $[209] !== flatColumns || $[210] !== gridSettings || $[211] !== handleClosePanel || $[212] !== handleFilterChange || $[213] !== handleResetFilters || $[214] !== store.setGridSettings || $[215] !== t61 || $[216] !== t62) {
    t63 = <Sidebar columns={flatColumns} columnConfig={columnConfig} filterState={t61} onFilterChange={handleFilterChange} onResetFilters={handleResetFilters} settings={gridSettings} onSettingsChange={store.setGridSettings} filterComponent={t62} activePanel={activePanel} onClose={handleClosePanel} />;
    $[207] = activePanel;
    $[208] = columnConfig;
    $[209] = flatColumns;
    $[210] = gridSettings;
    $[211] = handleClosePanel;
    $[212] = handleFilterChange;
    $[213] = handleResetFilters;
    $[214] = store.setGridSettings;
    $[215] = t61;
    $[216] = t62;
    $[217] = t63;
  } else {
    t63 = $[217];
  }
  let t64;
  if ($[218] !== t55 || $[219] !== t60 || $[220] !== t63) {
    t64 = <div className={t55}>{t60}{t63}</div>;
    $[218] = t55;
    $[219] = t60;
    $[220] = t63;
    $[221] = t64;
  } else {
    t64 = $[221];
  }
  let t65;
  if ($[222] !== gridContext.externalFilterId || $[223] !== gridContext.filterComponent) {
    t65 = gridContext.externalFilterId && gridContext.filterComponent && <ExternalFiltersPortal externalFilterId={gridContext.externalFilterId} filterComponent={gridContext.filterComponent} />;
    $[222] = gridContext.externalFilterId;
    $[223] = gridContext.filterComponent;
    $[224] = t65;
  } else {
    t65 = $[224];
  }
  let t66;
  if ($[225] !== t49 || $[226] !== t52 || $[227] !== t54 || $[228] !== t64 || $[229] !== t65) {
    t66 = <div className={t49}>{t52}{t54}{t64}{t65}</div>;
    $[225] = t49;
    $[226] = t52;
    $[227] = t54;
    $[228] = t64;
    $[229] = t65;
    $[230] = t66;
  } else {
    t66 = $[230];
  }
  return t66;
}

/**
 * Thin wrapper that owns the imperative handle (ref) for external refresh.
 *
 * Splitting ref handling into a separate wrapper is required because
 * EagGridTableProps contains `ref: RefObject<...>`, which causes React
 * Compiler's taint analysis to flag every `props.xxx` read as a ref access.
 * By isolating `useImperativeHandle` here and forwarding ref-free props to
 * EagGridTableInner via `refreshRef`, the inner component is completely
 * taint-free.
 */
function _temp21(colState_0) {
  return <col key={colState_0.key} style={{
    width: colState_0.width ?? DEFAULT_COL_WIDTH
  }} />;
}
function _temp20(colState) {
  return <col key={colState.key} style={{
    width: colState.width ?? DEFAULT_COL_WIDTH
  }} />;
}
function _temp19(cs_9) {
  return [cs_9.key, cs_9];
}
function _temp18(col_2) {
  return [col_2.key, col_2];
}
function _temp17(row_1) {
  return row_1.actions;
}
function _temp16(col_1) {
  return col_1 !== undefined;
}
function _temp15(col_0) {
  return [col_0.key, col_0];
}
function _temp14() {}
function _temp13() {}
function _temp12() {}
function _temp11() {}
function _temp10() {}
function _temp1() {
  return null;
}
function _temp0() {
  return "DESC" as const;
}
function _temp9() {
  return "ASC" as const;
}
function _temp8() {
  return "ASC" as const;
}
function _temp7(cs_6) {
  return [cs_6.key, cs_6];
}
function _temp6(col) {
  return [col.key, col];
}
function _temp5(cs_4) {
  return {
    columnKey: cs_4.key,
    order: cs_4.sort
  };
}
function _temp4(cs_3) {
  return cs_3.sort != null;
}
function _temp3(cs_1) {
  return [cs_1.key, cs_1.width ?? DEFAULT_COL_WIDTH];
}
function _temp2(cs_0) {
  return cs_0.pinned === "RIGHT";
}
function _temp(cs) {
  return cs.pinned !== "RIGHT";
}
export function EagGridTable(props: EagGridTableProps) {
  // refreshRef is written by EagGridTableInner (once serverDataRef and offsetRef are
  // ready) and read here by useImperativeHandle to expose refreshData to the consumer.
  const refreshRef = useRef<(() => void) | null>(null);
  useImperativeHandle(props.ref, () => ({
    refreshData: () => {
      refreshRef.current?.();
    }
  }));

  /* eslint-disable react-hooks/refs -- EagGridTableProps contains `ref` (React 19 ref-as-prop);
     all props.xxx reads in this return block are tainted false positives.
     This is the intentional boundary where ref-taint is isolated and not propagated further. */
  return <EagGridTableInner loadingCellRenderer={props.loadingCellRenderer} onPageChangeCallback={props.onPageChangeCallback} definition={props.definition} columnConfig={props.columnConfig} setVirtualPreset={props.setVirtualPreset} predefinedColumns={props.predefinedColumns} actionCallback={props.actionCallback} gridCode={props.gridCode} presetInstanceId={props.presetInstanceId} emptyState={props.emptyState} onRowSelectionChange={props.onRowSelectionChange} externalFilterId={props.externalFilterId} autoHeight={props.autoHeight} queryModifier={props.queryModifier} onFilterChanged={props.onFilterChanged} data-testid={props['data-testid']} refreshRef={refreshRef} />;
  /* eslint-enable react-hooks/refs */
}